import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Box, Chip } from '@material-ui/core';
import { CheckCircle, Error, TrendingUp, TrendingDown, Remove } from '@material-ui/icons';
import { CircularProgress } from '@mui/material';
import React from 'react';
import { ICsvPreviewInDTO } from '../../repository/jago/model/input/ICsvPreviewInDTO';
import { IMksImportInDTO } from '../../repository/jago/model/input/IMksImportInDTO';
import ReduxLanguage from '../ReduxLanguage';

export interface ICsvPreviewModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  preview: ICsvPreviewInDTO | null;
  filename: string;
  isLoading: boolean;
  lastImport?: IMksImportInDTO | {};
}

export class CsvPreviewModal extends React.Component<ICsvPreviewModalProps> {

  private getComparisonData() {
    const { preview, lastImport } = this.props;
    
    if (!preview || !lastImport || Object.keys(lastImport).length === 0) {
      return null;
    }

    const currentImport = lastImport as IMksImportInDTO;
    
    return {
      keys: {
        current: currentImport.keysImported || 0,
        new: preview.keysCount || 0,
        difference: (preview.keysCount || 0) - (currentImport.keysImported || 0)
      },
      cylinders: {
        current: currentImport.cylindersImported || 0,
        new: preview.cylindersCount || 0,
        difference: (preview.cylindersCount || 0) - (currentImport.cylindersImported || 0)
      },
      relations: {
        current: currentImport.relationsImported || 0,
        new: preview.relationsCount || 0,
        difference: (preview.relationsCount || 0) - (currentImport.relationsImported || 0)
      }
    };
  }

  private renderComparisonChip(label: string, current: number, newValue: number, difference: number) {
    let icon = <Remove />;
    let color: 'default' | 'primary' | 'secondary' = 'default';
    
    if (difference > 0) {
      icon = <TrendingUp />;
      color = 'primary';
    } else if (difference < 0) {
      icon = <TrendingDown />;
      color = 'secondary';
    }

    return (
      <Chip 
        icon={icon}
        label={
          <span>
            <ReduxLanguage languageKey={label} />: {current} → {newValue} 
            {difference !== 0 && (
              <span style={{ fontWeight: 'bold', marginLeft: 8 }}>
                ({difference > 0 ? '+' : ''}{difference})
              </span>
            )}
          </span>
        } 
        color={color}
      />
    );
  }

  public render() {
    const { open, onClose, onConfirm, preview, filename, isLoading, lastImport } = this.props;

    if (!preview) return null;

    const hasErrors = preview.errorRowsCount > 0;
    const canImport = preview.totalRowsProcessed > 0;

    return (
      <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              <ReduxLanguage languageKey="mks.csvPreviewTitle" />
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {filename}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* File Info */}
          <Box style={{ marginBottom: 24 }}>
            <Typography variant="h6" gutterBottom>
              <ReduxLanguage languageKey="mks.fileInfo" />
            </Typography>
            <Box style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Chip 
                label={
                  <span>
                    <ReduxLanguage languageKey="mks.fileSize" />: {Math.round(preview.fileSizeBytes / 1024)} KB
                  </span>
                } 
                color="default" 
              />
              <Chip 
                label={
                  <span>
                    <ReduxLanguage languageKey="mks.totalRows" />: {preview.totalRowsProcessed}
                  </span>
                } 
                color="primary" 
              />
              {hasErrors && (
                <Chip 
                  icon={<Error />} 
                  label={
                    <span>
                      <ReduxLanguage languageKey="mks.errors" />: {preview.errorRowsCount}
                    </span>
                  } 
                  color="secondary" 
                />
              )}
            </Box>
          </Box>

          {/* Import Summary */}
          <Box style={{ marginBottom: 24 }}>
            <Typography variant="h6" gutterBottom>
              <ReduxLanguage languageKey="mks.importSummary" />
            </Typography>
            <Box style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Chip 
                icon={<CheckCircle />} 
                label={
                  <span>
                    <ReduxLanguage languageKey="mks.keys" />: {preview.keysCount}
                  </span>
                } 
                color="primary" 
              />
              <Chip 
                icon={<CheckCircle />} 
                label={
                  <span>
                    <ReduxLanguage languageKey="mks.cylinders" />: {preview.cylindersCount}
                  </span>
                } 
                color="primary" 
              />
              <Chip 
                icon={<CheckCircle />} 
                label={
                  <span>
                    <ReduxLanguage languageKey="mks.relations" />: {preview.relationsCount}
                  </span>
                } 
                color="primary" 
              />
            </Box>
          </Box>

          {/* Comparison with Current Configuration */}
          {(() => {
            const comparisonData = this.getComparisonData();
            if (!comparisonData) return null;

            return (
              <Box style={{ marginBottom: 24 }}>
                <Typography variant="h6" gutterBottom>
                  <ReduxLanguage languageKey="mks.comparisonTitle" />
                </Typography>
                <Box style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {this.renderComparisonChip('mks.keys', comparisonData.keys.current, comparisonData.keys.new, comparisonData.keys.difference)}
                  {this.renderComparisonChip('mks.cylinders', comparisonData.cylinders.current, comparisonData.cylinders.new, comparisonData.cylinders.difference)}
                  {this.renderComparisonChip('mks.relations', comparisonData.relations.current, comparisonData.relations.new, comparisonData.relations.difference)}
                </Box>
              </Box>
            );
          })()}

          {hasErrors && (
            <Box style={{ marginBottom: 16 }}>
              <Typography variant="body2" color="error">
                <ReduxLanguage languageKey="mks.csvHasErrors" />
              </Typography>
            </Box>
          )}

          {!canImport && (
            <Box style={{ marginTop: 16 }}>
              <Typography variant="body2" color="error">
                <ReduxLanguage languageKey="mks.noValidRowsToImport" />
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            <ReduxLanguage languageKey="forms.cancel" />
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            color="primary"
            disabled={!canImport || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            <ReduxLanguage languageKey="mks.confirmImport" />
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default CsvPreviewModal;
